// Résolution des cibles de notification push
// Utilisé par NotificationsPage pour :
// 1. Lister les options (équipes favorites déclarées, villes connues, bars favoris courants)
// 2. Calculer le nombre de destinataires d'un envoi donné (preview avant confirm)
// 3. Récupérer la liste des push_tokens pour le fallback direct Expo

import { supabaseService } from '../lib/supabaseService';

export type TargetType = 'all' | 'city' | 'team' | 'bar';

export interface TargetFilter {
  type: TargetType;
  value?: string; // team key, city name, or bar id
}

export interface TargetProfile {
  id: string;
  push_token: string | null;
  city: string | null;
  favorite_teams: string[] | null;
  favorite_bars: string[] | null;
}

// Liste des clés d'équipes connues (miroir scripts/send-notifications.js)
export const KNOWN_TEAM_KEYS: Array<{ key: string; label: string }> = [
  { key: 'psg', label: 'Paris SG' },
  { key: 'om', label: 'Marseille' },
  { key: 'losc', label: 'LOSC Lille' },
  { key: 'ol', label: 'Lyon' },
  { key: 'monaco', label: 'Monaco' },
  { key: 'lens', label: 'Lens' },
  { key: 'rennes', label: 'Rennes' },
  { key: 'nice', label: 'Nice' },
  { key: 'arsenal', label: 'Arsenal' },
  { key: 'liverpool', label: 'Liverpool' },
  { key: 'mancity', label: 'Man City' },
  { key: 'real', label: 'Real Madrid' },
  { key: 'barca', label: 'FC Barcelona' },
  { key: 'bayern', label: 'Bayern Munich' },
  { key: 'france_foot', label: 'France (foot)' },
  { key: 'france_rugby', label: 'France (rugby)' },
];

// Récupère tous les profils prêts à recevoir une notification
// (push_token non null + notifications_enabled=true)
async function fetchEligibleProfiles(): Promise<TargetProfile[]> {
  const { data, error } = await supabaseService
    .from('profiles')
    .select('id, push_token, city, favorite_teams, favorite_bars')
    .not('push_token', 'is', null)
    .eq('notifications_enabled', true);
  if (error) throw error;
  return (data || []) as TargetProfile[];
}

// Filtre les profils selon la cible, retourne les push_tokens valides
export function filterProfiles(profiles: TargetProfile[], filter: TargetFilter): TargetProfile[] {
  if (filter.type === 'all') return profiles;
  if (filter.type === 'city') {
    const city = filter.value?.trim().toLowerCase();
    if (!city) return profiles;
    return profiles.filter(p => (p.city || '').toLowerCase() === city);
  }
  if (filter.type === 'team') {
    const team = filter.value;
    if (!team) return profiles;
    return profiles.filter(p => (p.favorite_teams || []).includes(team));
  }
  if (filter.type === 'bar') {
    const barId = filter.value;
    if (!barId) return profiles;
    return profiles.filter(p => (p.favorite_bars || []).includes(barId));
  }
  return profiles;
}

export async function countMatching(filter: TargetFilter): Promise<number> {
  const profiles = await fetchEligibleProfiles();
  return filterProfiles(profiles, filter).length;
}

export async function resolveTokens(filter: TargetFilter): Promise<string[]> {
  const profiles = await fetchEligibleProfiles();
  return filterProfiles(profiles, filter)
    .map(p => p.push_token)
    .filter((t): t is string => typeof t === 'string' && t.length > 0);
}

// Lecture des villes distinctes présentes dans la base (pour le select)
export async function listKnownCities(): Promise<string[]> {
  const { data, error } = await supabaseService
    .from('profiles')
    .select('city')
    .not('city', 'is', null);
  if (error) throw error;
  const set = new Set<string>();
  for (const row of (data || []) as Array<{ city: string | null }>) {
    if (row.city && row.city.trim()) set.add(row.city.trim());
  }
  // Lille forcé en premier si présent, sinon on l'ajoute (défaut projet)
  const arr = Array.from(set).sort();
  if (!arr.includes('Lille')) arr.unshift('Lille');
  else {
    arr.splice(arr.indexOf('Lille'), 1);
    arr.unshift('Lille');
  }
  return arr;
}

export interface BarOption {
  id: string;
  name: string;
}

// Bars visibles (cohérent avec feature 1) pour le sélecteur de cible "favori"
export async function listBarsForTargeting(): Promise<BarOption[]> {
  const { data, error } = await supabaseService
    .from('bars')
    .select('id, name')
    .eq('is_visible', true)
    .order('name');
  if (error) throw error;
  return (data || []) as BarOption[];
}
